import { z } from 'zod'
import { DAY, WEEK } from '../../util/time'
import { type GameData } from './game'

/**
 * Where a competition sits in the viewer's list, declared in display order: current first, then recently ended, then
 * upcoming. `upcomingLater` and `earlier` are everything the dashboard widget leaves out, so the competitions list can
 * still reach it.
 */
export const GAME_RELEVANCE_GROUPS = ['current', 'recentlyEnded', 'upcomingSoon', 'upcomingLater', 'earlier'] as const
export const GameRelevanceGroupSchema = z.enum(GAME_RELEVANCE_GROUPS)
export type GameRelevanceGroup = z.infer<typeof GameRelevanceGroupSchema>

/**
 * Why the viewer sees a competition. A creator who also competes is a `participant`. `permission` is everything
 * else: the viewer neither competes in it nor created it, and sees it because their role lets them, as a manager
 * does for their downline or an admin for the whole company.
 */
export const GameViewerRelationSchema = z.enum(['participant', 'creator', 'permission'])
export type GameViewerRelation = z.infer<typeof GameViewerRelationSchema>

export const GAME_RECENTLY_ENDED_WINDOW = WEEK
export const GAME_UPCOMING_SOON_WINDOW = DAY

/** The groups the dashboard widget shows, and the most competitions it shows. */
export const GAME_WIDGET_GROUPS: GameRelevanceGroup[] = ['current', 'recentlyEnded', 'upcomingSoon']
export const GAME_WIDGET_LIMIT = 10

type GameRelevanceWindow = Pick<GameData, 'status' | 'startDate' | 'endDate'>
export type GameRelevanceFields = GameRelevanceWindow & Pick<GameData, 'gameId'>

export type RelevantGame<T extends GameRelevanceFields = GameRelevanceFields> = {
  game: T
  group: GameRelevanceGroup
}

/**
 * A game ended early keeps its scheduled `endDate`, so it stays "recently ended" until a week after that date. Drafts
 * are never current or soon: nobody is competing in them yet.
 */
export function getGameRelevanceGroup(game: GameRelevanceWindow, now: number): GameRelevanceGroup {
  switch (game.status) {
    case 'active':
      return 'current'
    case 'finished':
      return game.endDate >= now - GAME_RECENTLY_ENDED_WINDOW ? 'recentlyEnded' : 'earlier'
    case 'published':
      return game.startDate <= now + GAME_UPCOMING_SOON_WINDOW ? 'upcomingSoon' : 'upcomingLater'
    case 'draft':
      return 'upcomingLater'
  }
}

/**
 * Orders games by group, then by what matters most inside it: the ones ending soonest, the ones that ended most
 * recently, the ones starting soonest. Ties fall back to the id so a page boundary never repeats or skips a game.
 */
export function rankGamesByRelevance<T extends GameRelevanceFields>(games: T[], now: number): RelevantGame<T>[] {
  return games.map((game) => ({ game, group: getGameRelevanceGroup(game, now) })).sort(compareRelevantGames)
}

function compareRelevantGames(a: RelevantGame, b: RelevantGame): number {
  const byGroup = GAME_RELEVANCE_GROUPS.indexOf(a.group) - GAME_RELEVANCE_GROUPS.indexOf(b.group)
  if (byGroup !== 0) return byGroup
  const byDate = compareWithinGroup(a, b)
  if (byDate !== 0) return byDate
  return a.game.gameId.localeCompare(b.game.gameId)
}

function compareWithinGroup(a: RelevantGame, b: RelevantGame): number {
  switch (a.group) {
    case 'current':
      return a.game.endDate - b.game.endDate
    case 'recentlyEnded':
    case 'earlier':
      return b.game.endDate - a.game.endDate
    case 'upcomingSoon':
    case 'upcomingLater':
      return a.game.startDate - b.game.startDate
  }
}
